import {ChangeDetectorRef, Component, OnInit} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import {map, take} from 'rxjs/operators';
import {forkJoin, of} from 'rxjs';
import {BasicPageService, PageEntity} from 'fuse-core/components/basic-page/basic-page.service';
import {DomSanitizer, SafeHtml} from '@angular/platform-browser';
import {AuthenticationService} from '@modules/authentication';
import {ModelSchema, Structures} from 'octopus-model';
import {modulesSettings} from '../../../app/settings';
import {get} from 'lodash-es';
import {CommunicationCenterService} from '@modules/communication-center';

const settingsInfos: ModelSchema = new ModelSchema({
    pagesGar: Structures.array([
        {id: 'mentions-legales-gar', icon: 'policy-rounded-fill'},
        {id: 'cgurgpd-gar', icon: 'article-rounded-fill'},
        {id: 'politique-confidentialite', icon: 'lock-rounded-fill'},
        {id: 'accessibilite', icon: 'accessibility-rounded'},
        {id: 'feedback', icon: 'markunread-mailbox-rounded-fill'}]),
    pagesNotGar: Structures.array([
        {id: 'mentions-legales', icon: 'policy-rounded-fill'},
        {id: 'cgurgpd', icon: 'article-rounded-fill'},
        {id: 'politique-confidentialite', icon: 'lock-rounded-fill'},
        {id: 'accessibilite', icon: 'accessibility-rounded'},
        {id: 'feedback', icon: 'markunread-mailbox-rounded-fill'}]),

});

@Component({
    selector: 'app-informations-legales',
    templateUrl: './informations-legales.component.html',
    standalone: false
})
export class InformationsLegalesComponent implements OnInit {
    public showLoader = false;
    private settings: { [key: string]: any };
    public pagesReady: {
        icon: string,
        data: PageEntity,
        title: SafeHtml,
        body: SafeHtml,
        children: { data: PageEntity, title: SafeHtml, body: SafeHtml }[]
    }[] = [];
    public title = '';
    public body = '';

    constructor(
        private translate: TranslateService,
        private pageService: BasicPageService,
        public domSanitizer: DomSanitizer,
        private auth: AuthenticationService,
        private changeDetectorRef: ChangeDetectorRef,
        private communicationCenter: CommunicationCenterService,
    ) {
        this.settings = settingsInfos.filterModel(modulesSettings.infoLegales);
        this.communicationCenter.getRoom('authentication')
            .getSubject('userData')
            .pipe(take(1))
            .subscribe(() => {
                this.getPages();
            });
    }

    public ngOnInit(): void {
        this.showLoader = true;
        this.getPages();
    }

    getPages(): void {
        const roots = this.auth.isGAR() ? this.pagesGar : this.pagesNotGar;
        const rootIds = roots.map(p => p.id);
        const iconMap = new Map(roots.map(p => [p.id, p.icon]));

        if (!rootIds.length) {
            this.pagesReady = [];
            this.showLoader = false;
            return;
        }

        forkJoin(rootIds.map(id => this.pageService.loadPage(id))).subscribe(rootPages => {
            const perRoot$ = rootPages.map((rootPage: any, index) => {
                const subPageIds = (rootPage?.get('pages') ?? [])
                    .map((p: any) => {
                        if (p == null) {
                            return null;
                        }
                        if (typeof p === 'string' || typeof p === 'number') {
                            return p;
                        }
                        return p?.target_id ?? p?.id ?? null;
                    })
                    .filter((id: any) => !!id);
                const icon = iconMap.get(rootIds[index]) ?? '';

                if (!subPageIds.length) {
                    return of({
                        rootPage,
                        icon,
                        subPages: []
                    });
                }

                return forkJoin(subPageIds.map(id => this.pageService.loadPage(id))).pipe(
                    map(subPages => ({
                        rootPage,
                        icon,
                        subPages
                    }))
                );
            });

            forkJoin(perRoot$).subscribe(items => {
                this.pagesReady = items.map((item: any) => ({
                    data: item.rootPage,
                    title: this.domSanitizer.bypassSecurityTrustHtml(item.rootPage?.get('label')),
                    body: this.domSanitizer.bypassSecurityTrustHtml(item.rootPage?.get('body')),
                    icon: item.icon,
                    children: item.subPages.map((p: any) => ({
                        data: p,
                        title: this.domSanitizer.bypassSecurityTrustHtml(p?.get('label')),
                        body: this.domSanitizer.bypassSecurityTrustHtml(p?.get('body'))
                    }))
                }));
                this.showLoader = false;
            });
        });
    }

    public get pagesGar(): { id: string, icon: string }[] {
        return get(this.settings, 'pagesGar', []);
    }

    public get pagesNotGar(): { id: string, icon: string }[] {
        return get(this.settings, 'pagesNotGar', []);
    }

    focus(index: number): void {
        const element = document.getElementById('focus_' + index);
        if (element) {
            this.changeDetectorRef.detectChanges();
            element.scrollIntoView({behavior: 'auto', block: 'start', inline: 'nearest'});
        }
    }

}
